import { useCallback, useMemo, useState } from 'react';
import { RSVP_STATUS } from '.';
import { EventsQuery, HouseholdQuery, useEventsQuery } from '../../../generated/graphql';
import { useSequenceState } from '../Sequences/useSequenceState';

type HouseholdType = HouseholdQuery['households'];
type InvitationType = HouseholdType[0]['people'][0]['invitations'][0];

export type SetFilterCriteria = (rsvpValue: string | null, eventName: string | null) => void;

export const mapEventNameToEventId = (events: EventsQuery['events']): Record<string, string> => {
  if (!events.length) return {};

  return events.reduce(
    (mapped, evt) => ({
      ...mapped,
      [`${evt.name}`]: evt.id,
    }),
    {}
  );
};

export const rsvpStatusStringToValue = (rsvpStatusString: string) => {
  // accepted = true
  // declined = false
  // invited = null
  return rsvpStatusString
    ? rsvpStatusString === RSVP_STATUS.NO_RESPONSE
      ? null
      : rsvpStatusString === RSVP_STATUS.ACCEPTED
    : undefined;
};

export const useFilteredHouseholds = () => {
  const { householdData } = useSequenceState();
  const { data: eventData } = useEventsQuery();
  const [rsvpStatusFilterValue, setRsvpStatusFilterValue] = useState<boolean | null | undefined>();
  const [eventIdFilterValue, setEventIdFilterValue] = useState<string | null>(null);

  const households = householdData?.households;
  const eventNameToEventIdMap = useMemo(() => mapEventNameToEventId(eventData?.events || []), [eventData]);

  const filterHouseholdByInvitationCriteria = useCallback(
    (criteria: (invite: InvitationType) => boolean) => {
      if (!households?.length || households === null) return households;

      return households.filter(house => {
        return house?.people?.some(person => person.invitations?.some(criteria));
      });
    },
    [households]
  );

  const setFilterCriteria: SetFilterCriteria = useCallback(
    (rsvpString: string | null, eventName: string | null) => {
      const rsvpValue = rsvpStatusStringToValue(rsvpString || '');
      if (rsvpValue !== rsvpStatusFilterValue) setRsvpStatusFilterValue(rsvpValue);

      const eventId = eventName ? eventNameToEventIdMap[eventName] : undefined;
      if (eventId !== eventIdFilterValue) setEventIdFilterValue(eventId || null);
    },
    [eventIdFilterValue, eventNameToEventIdMap, rsvpStatusFilterValue]
  );

  const isRsvpStatusValue = useCallback(value => value === null || value === true || value === false, []);

  // if no response: null if accepted true else false for declined
  const filterCriteria = useMemo(() => {
    return isRsvpStatusValue(rsvpStatusFilterValue) && eventIdFilterValue
      ? (invite: InvitationType) => invite.rsvp === rsvpStatusFilterValue && invite.eventId === eventIdFilterValue
      : isRsvpStatusValue(rsvpStatusFilterValue) && !eventIdFilterValue
      ? (invite: InvitationType) => invite.rsvp === rsvpStatusFilterValue
      : !isRsvpStatusValue(rsvpStatusFilterValue) && eventIdFilterValue
      ? (invite: InvitationType) => invite.eventId === eventIdFilterValue
      : null;
  }, [eventIdFilterValue, isRsvpStatusValue, rsvpStatusFilterValue]);

  const filteredHouseholds = useMemo(() => {
    // no filter return householdData
    if (!filterCriteria) return householdData?.households || [];

    return filterHouseholdByInvitationCriteria(filterCriteria) || [];
  }, [filterCriteria, filterHouseholdByInvitationCriteria, householdData]);

  const hookReturn = useMemo(() => ({ filteredHouseholds, setFilterCriteria }), [
    filteredHouseholds,
    setFilterCriteria,
  ]);
  return hookReturn;
};
